'use client'

import React, { useMemo, useState } from 'react'
import Image from 'next/image'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'
import { outfit } from '@/fonts'
import { normalizeThirdPartySource } from '@/utilities/thirdPartyReviews/normalizeSource'
import type {
  ThirdPartyReviewDisplay,
  ThirdPartyReviewSource,
} from '@/utilities/thirdPartyReviews/types'

const SOURCE_TABS: Array<{
  id: ThirdPartyReviewSource
  label: string
  logoSrc: string
}> = [
  { id: 'google', label: 'Google', logoSrc: '/homepage/google.png' },
  { id: 'yelp', label: 'Yelp', logoSrc: '/homepage/yelp.png' },
  { id: 'houzz', label: 'Houzz', logoSrc: '/homepage/houzz.png' },
]

const CARD_SOURCE_LOGO: Record<ThirdPartyReviewSource, string> = {
  google: '/homepage/google-colored.png',
  yelp: '/homepage/yelp-colored.png',
  houzz: '/homepage/houzz-colored.png',
}

function firstSourceWithData(reviews: ThirdPartyReviewDisplay[]): ThirdPartyReviewSource {
  const hit = SOURCE_TABS.find((s) => reviews.some((r) => r.source === s.id))
  return hit?.id ?? 'google'
}

function lexicalNodeHasText(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false
  const n = node as Record<string, unknown>
  if (typeof n.text === 'string' && n.text.trim().length > 0) return true
  if (Array.isArray(n.children)) return n.children.some(lexicalNodeHasText)
  return false
}

function headingIsPresent(heading: unknown): boolean {
  if (typeof heading === 'string') return heading.trim().length > 0
  if (!heading || typeof heading !== 'object') return false
  return lexicalNodeHasText((heading as { root?: unknown }).root)
}

function SourceBadge({ source }: { source: ThirdPartyReviewSource }) {
  const src = CARD_SOURCE_LOGO[source]
  return (
    <span className="relative block h-[24px] w-[24px] shrink-0">
      <Image alt="Review platform logo" className="object-contain" fill sizes="20px" src={src} />
    </span>
  )
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 20 22"
      fill={filled ? 'rgb(253, 211, 20)' : '#e0e0e0'}
      style={{ display: 'inline-block' }}
    >
      <path d="M20.429 8.14c0 0.221-0.16 0.43-0.319 0.589l-4.456 4.346 1.056 6.138c0.012 0.086 0.012 0.16 0.012 0.246 0 0.319-0.147 0.614-0.503 0.614-0.172 0-0.344-0.061-0.491-0.147l-5.512-2.897-5.512 2.897c-0.16 0.086-0.319 0.147-0.491 0.147-0.356 0-0.516-0.295-0.516-0.614 0-0.086 0.012-0.16 0.025-0.246l1.056-6.138-4.469-4.346c-0.147-0.16-0.307-0.368-0.307-0.589 0-0.368 0.381-0.516 0.687-0.565l6.163-0.896 2.762-5.586c0.11-0.233 0.319-0.503 0.602-0.503s0.491 0.27 0.602 0.503l2.762 5.586 6.163 0.896c0.295 0.049 0.688 0.196 0.688 0.565z" />
    </svg>
  )
}

function Stars({ rating }: { rating: number }) {
  const count = Math.max(0, Math.min(5, Math.round(rating)))
  return (
    <div aria-label={`${count} out of 5 stars`} className="mt-[1px] flex items-center gap-[2px]">
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon key={i} filled={i < count} />
      ))}
    </div>
  )
}

function ReviewText({
  id,
  text,
  expanded,
  onToggle,
}: {
  id: string
  text: string
  expanded: boolean
  onToggle: (id: string) => void
}) {
  const trimmed = text.trim()
  const pRef = React.useRef<HTMLParagraphElement>(null)
  const [isOverflowing, setIsOverflowing] = React.useState(false)
  const [collapsedH, setCollapsedH] = React.useState(0)
  const [fullH, setFullH] = React.useState(0)

  React.useEffect(() => {
    const el = pRef.current
    if (!el) return
    const measure = () => {
      const cs = window.getComputedStyle(el)
      let lineHeight = parseFloat(cs.lineHeight)
      if (Number.isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.375
      const collapsed = lineHeight * 3
      const full = el.scrollHeight
      setCollapsedH(collapsed)
      setFullH(full)
      setIsOverflowing(full > collapsed + 1)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [trimmed])

  if (!trimmed) return null

  const targetH = expanded ? fullH : collapsedH ? Math.min(collapsedH, fullH || collapsedH) : 0

  return (
    <div className="text-[17px] leading-snug text-[#1e1e1e]">
      <div
        className="overflow-hidden transition-[max-height] duration-700 ease-in-out"
        style={{ maxHeight: targetH ? `${targetH}px` : '4.2em' }}
      >
        <p ref={pRef}>{trimmed}</p>
      </div>
      {isOverflowing || expanded ? (
        <button
          className="inline-block text-left text-[17px] font-normal text-[#009bab] hover:underline"
          onClick={() => onToggle(id)}
          type="button"
        >
          {expanded ? 'read less' : 'read more'}
        </button>
      ) : null}
    </div>
  )
}

function ReviewCard({
  review,
  expanded,
  onToggle,
}: {
  review: ThirdPartyReviewDisplay
  expanded: boolean
  onToggle: (id: string) => void
}) {
  const rating = review.rating || 0

  return (
    <article className="relative border border-[#eeeeee] bg-white px-[20px] pt-[15px] pb-[10px] shadow-[0px_0px_10px_2px_rgba(0,0,0,0.14)]">
      <div className="absolute top-[5px] right-[5px]">
        <SourceBadge source={review.source} />
      </div>
      <div className="min-w-0 flex h-[80px] flex-col justify-start">
        <h3 className="truncate text-[20px] font-bold leading-tight text-black">
          {review.reviewerName}
        </h3>
        <Stars rating={rating} />
      </div>
      <ReviewText
        expanded={expanded}
        id={review.key}
        onToggle={onToggle}
        text={review.reviewText || ''}
      />
    </article>
  )
}

export default function ThirdPartyReviewsSection({
  heading,
  reviews,
}: {
  heading?: unknown
  reviews: ThirdPartyReviewDisplay[]
}) {
  const rows = useMemo(
    () =>
      reviews.map((r) => ({
        ...r,
        source: normalizeThirdPartySource(r.source),
      })),
    [reviews],
  )

  const reviewsSyncKey = useMemo(() => rows.map((r) => `${r.key}:${r.source}`).join('|'), [rows])

  const [activeTab, setActiveTab] = useState<ThirdPartyReviewSource>(() =>
    firstSourceWithData(rows),
  )
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [visibleCount, setVisibleCount] = useState(4)

  // Reset tab/paging state when the review list contents change (render-phase adjustment).
  const [prevSyncKey, setPrevSyncKey] = useState(reviewsSyncKey)
  if (prevSyncKey !== reviewsSyncKey) {
    setPrevSyncKey(reviewsSyncKey)
    setActiveTab(firstSourceWithData(rows))
    setVisibleCount(4)
    setExpandedIds([])
  }

  const filtered = useMemo(() => rows.filter((r) => r.source === activeTab), [activeTab, rows])
  const visible = filtered.slice(0, visibleCount)
  const showLoadMore = visibleCount < filtered.length

  if (rows.length === 0) return null
  const hasHeading = headingIsPresent(heading)

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  const switchTab = (tab: ThirdPartyReviewSource) => {
    setActiveTab(tab)
    setVisibleCount(4)
    setExpandedIds([])
  }

  return (
    <section id="testimonials" className="container-fluid mx-auto p-[20px] md:px-[30px] pt-[50px]">
      {hasHeading ? (
        <div className="mb-8 md:mb-10">
          {typeof heading === 'string' ? (
            <h2 className="max-w-2xl text-3xl font-normal tracking-tight text-foreground md:text-4xl">
              {heading.trim()}
            </h2>
          ) : (
            <RichText
              className="max-w-2xl text-3xl font-normal tracking-tight text-foreground md:text-4xl [&_p]:mb-2 [&_p:last-child]:mb-0"
              data={heading as DefaultTypedEditorState}
              enableGutter={false}
              enableProse={false}
            />
          )}
        </div>
      ) : null}

      <div className="mx-auto">
        <div className="mb-5 flex md:mb-[20px] flex-nowrap items-center justify-start gap-4 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mb-6 md:justify-center">
          {SOURCE_TABS.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                className={cn(
                  'group flex min-w-[120px] h-[49px] items-center justify-center gap-2 border px-[30px] py-[10px] text-[18px] font-semibold transition-colors duration-200',
                  'border-[#1595a7] bg-white text-black',
                  'hover:bg-[#009BAB] hover:text-white',
                  isActive && 'bg-[#009BAB] text-white',
                )}
                onClick={() => switchTab(tab.id)}
                type="button"
              >
                <span className="relative block size-[18px] shrink-0">
                  <Image
                    alt="Review platform logo"
                    className={cn(
                      'object-contain transition-[filter] duration-200',
                      isActive && 'brightness-0 invert',
                      !isActive && 'group-hover:brightness-0 group-hover:invert',
                    )}
                    fill
                    sizes="18px"
                    src={tab.logoSrc}
                  />
                </span>
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        {visible.length === 0 ? (
          <p className="py-10 text-center text-[16px] text-foreground/70">
            No reviews in this tab yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-x-[20px] gap-y-[30px] lg:grid-cols-2 p-[10px]">
            {visible.map((review) => (
              <ReviewCard
                key={review.key}
                expanded={expandedIds.includes(review.key)}
                onToggle={toggleExpanded}
                review={review}
              />
            ))}
          </div>
        )}

        {showLoadMore ? (
          <div className="mt-[30px] flex justify-center">
            <div className="flex flex-col items-center">
              <button
                className={cn(
                  outfit.className,
                  'border border-[#009BAB] bg-white px-6 py-2 text-[21px] font-medium leading-[32px] text-[#1d1d1b] lowercase transition-colors hover:bg-[#009BAB] hover:text-white',
                )}
                onClick={() => setVisibleCount((n) => n + 4)}
                type="button"
              >
                load more
              </button>
              <div className="h-[8px] w-full bg-[#009BAB]" />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
